import React from 'react';
import { Box, styled, useTheme } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import {
  BloodGlucoseWeekSeries,
  MEAL_COLORS,
  MealReading,
} from 'features/progress/utils/bloodGlucoseMeals';
import {
  EMPTY_Y_AXIS,
  ShapedMarkElement,
  formatDayLabel,
  getChartXAxis,
  getYAxisTicks,
  isAnchorIndex,
  mgPerDl,
  withAnchorPoint,
} from 'features/progress/utils/bloodGlucoseChartUtils';

const LAYOUT = {
  mobile: { height: 220, margin: { top: 20, right: 26, bottom: 24, left: 43 }, tick: 'caption' },
  wide: { height: 321, margin: { top: 24, right: 44, bottom: 35, left: 88 }, tick: 'title3' },
} as const;

const SPACING = {
  mobile: { xLabelGap: 0, legendGap: 2, yLabelGap: 8 },
  wide: { xLabelGap: 8, legendGap: 6, yLabelGap: 12 },
} as const;

const CARD_PADDING = 16;
const TITLE_LEFT = { mobile: 52, wide: 78 } as const;
const PLOT_RIGHT = { mobile: 36, wide: 44 } as const;

const Root = styled(Box, { shouldForwardProp: (prop) => prop !== 'bleed' })<{ bleed: boolean }>(
  ({ theme, bleed }) => ({
    ...(bleed ? { marginInline: theme.spacing(-2) } : { width: '100%' }),
    '& .MuiChartsAxis-line': { stroke: theme.palette.grey[500], strokeWidth: 2 },
    '& .MuiChartsAxis-tick': { display: 'none' },
  }),
);

const LegendSlot = styled(Box, { shouldForwardProp: (prop) => prop !== 'gap' })<{ gap: number }>(
  ({ theme, gap }) => ({ marginTop: theme.spacing(gap) }),
);

export interface BloodGlucoseChartProps {
  series: BloodGlucoseWeekSeries;
  meals: MealReading[];
  /** From `getYAxisRange` over the full history. */
  yRange: { min: number; max: number };
  legend?: React.ReactNode;
  /** Widget only: align the y-axis with the card title. */
  alignToTitle?: boolean;
}

export const BloodGlucoseChart = ({
  series,
  meals,
  yRange,
  legend,
  alignToTitle = false,
}: BloodGlucoseChartProps) => {
  const theme = useTheme();
  const { isMobile } = useDeviceType();
  const size = isMobile ? 'mobile' : 'wide';
  const layout = LAYOUT[size];
  const spacing = SPACING[size];
  const margin = alignToTitle
    ? { ...layout.margin, left: TITLE_LEFT[size], right: PLOT_RIGHT[size] }
    : layout.margin;
  const { days, byMeal, hasReadings } = series;
  const { xAxis, hasAnchor } = getChartXAxis(days, formatDayLabel);
  const { min, max, step } = hasReadings ? { ...yRange, step: undefined } : EMPTY_Y_AXIS;

  const tickVariant = theme.typography[layout.tick];
  const tickLabelStyle = {
    fontFamily: theme.typography.fontFamily,
    fontSize: tickVariant.fontSize,
    lineHeight: tickVariant.lineHeight,
    fontWeight: 400,
    letterSpacing: 0,
    fill: theme.palette.grey[800],
  };

  return (
    <Root bleed={alignToTitle}>
      <LineChart
        height={layout.height}
        margin={margin}
        slots={{ mark: ShapedMarkElement, legend: () => null, noDataOverlay: () => null }}
        series={meals.map((meal) => ({
          id: meal,
          data: withAnchorPoint(byMeal[meal], hasAnchor),
          color: MEAL_COLORS[meal],
          curve: 'linear' as const,
          connectNulls: true,
          showMark: ({ index }: { index: number }) => !isAnchorIndex(index, hasAnchor),
          valueFormatter: mgPerDl,
        }))}
        xAxis={[
          {
            ...xAxis,
            tickSize: spacing.xLabelGap,
            disableTicks: true,
            tickLabelStyle: hasReadings ? tickLabelStyle : { display: 'none' },
          },
        ]}
        yAxis={[
          {
            min,
            max,
            tickInterval: getYAxisTicks(min, max, step),
            tickSize: spacing.yLabelGap - 2,
            tickLabelStyle,
          },
        ]}
        grid={{ horizontal: true, vertical: true }}
        tooltip={hasReadings ? undefined : { trigger: 'none' }}
        axisHighlight={hasReadings ? undefined : { x: 'none' }}
      />
      {legend && <LegendSlot gap={spacing.legendGap}>{legend}</LegendSlot>}
    </Root>
  );
};
